/**
 * DOM-basierter Weltenauswahl-Screen.
 * Zeigt verfügbare Welten nach dem Login, vor dem Ladebildschirm.
 */
import { createDiv, createButton } from './LoginFormElements';
import type { WorldSummary } from '../world/PlayerWorldTypes';

type WorldCallback = (worldId: string) => void;

export class WorldSelectScreen {
  private _root!: HTMLDivElement;
  private _card!: HTMLDivElement;
  private _list!: HTMLDivElement;
  private _errorEl!: HTMLDivElement;
  private _onSelect: WorldCallback | null = null;
  private _joinedIds = new Set<string>();
  private _buttons: HTMLButtonElement[] = [];

  constructor() {
    this._build();
  }

  show(): void {
    document.body.appendChild(this._root);
    const topGap = (this._root.clientHeight - this._card.offsetHeight) / 2;
    this._root.style.alignItems = 'flex-start';
    this._root.style.paddingTop = `${Math.max(topGap, 32)}px`;
  }

  hide(): void {
    this._root.style.opacity = '0';
    setTimeout(() => this._root.remove(), 400);
  }

  onWorldSelected(cb: WorldCallback): void { this._onSelect = cb; }

  setJoinedWorlds(ids: string[]): void {
    this._joinedIds = new Set(ids);
  }

  setWorlds(worlds: WorldSummary[]): void {
    this._list.innerHTML = '';
    this._buttons = [];

    if (worlds.length === 0) {
      const msg = createDiv({
        color: '#888', fontFamily: 'monospace', fontSize: '13px',
        textAlign: 'center', padding: '16px 0',
      });
      msg.textContent = 'Keine Welten verfügbar';
      this._list.appendChild(msg);
      return;
    }

    for (const world of worlds) {
      this._list.appendChild(this._createWorldCard(world));
    }
  }

  showError(msg: string): void {
    this._errorEl.textContent = msg;
    this._errorEl.style.display = msg ? 'block' : 'none';
  }

  setLoading(loading: boolean): void {
    for (const btn of this._buttons) {
      btn.disabled = loading;
      btn.style.opacity = loading ? '0.5' : '1';
    }
  }

  private _build(): void {
    this._root = createDiv({
      position: 'fixed', inset: '0', zIndex: '10001', background: '#000',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Segoe UI', system-ui, sans-serif", transition: 'opacity 0.4s',
    });

    this._card = createDiv({
      maxWidth: '400px', width: '90%', padding: 'clamp(20px,4vw,32px)',
      background: 'rgba(10,12,20,0.95)', border: '1px solid rgba(136,204,255,0.15)',
      borderRadius: '12px',
    });
    this._root.appendChild(this._card);

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: 'clamp(16px,4vw,20px)', fontWeight: '300', letterSpacing: '4px',
      textTransform: 'uppercase', color: '#8cf', textAlign: 'center', marginBottom: '8px',
    });
    title.textContent = 'Imperial Star Rising';
    this._card.appendChild(title);

    const subtitle = document.createElement('div');
    Object.assign(subtitle.style, {
      fontSize: '13px', color: '#556', fontFamily: 'monospace',
      textAlign: 'center', marginBottom: '24px', letterSpacing: '1px',
    });
    subtitle.textContent = 'Welt auswählen';
    this._card.appendChild(subtitle);

    this._errorEl = createDiv({
      display: 'none', color: '#f44', fontFamily: 'monospace', fontSize: '12px',
      padding: '8px', marginBottom: '12px', textAlign: 'center',
    });
    this._card.appendChild(this._errorEl);

    this._list = createDiv({ display: 'flex', flexDirection: 'column', gap: '10px' });
    this._card.appendChild(this._list);
  }

  private _createWorldCard(world: WorldSummary): HTMLDivElement {
    const joined = this._joinedIds.has(world.id);
    const card = createDiv({
      padding: '14px', background: 'rgba(20,25,40,0.8)',
      border: joined ? '1px solid rgba(100,255,150,0.3)' : '1px solid rgba(136,204,255,0.1)',
      borderRadius: '8px',
    });

    const name = document.createElement('div');
    Object.assign(name.style, {
      fontSize: '15px', color: '#8cf', fontFamily: 'monospace',
      marginBottom: '4px', letterSpacing: '1px',
    });
    name.textContent = world.name;
    card.appendChild(name);

    const info = document.createElement('div');
    Object.assign(info.style, {
      fontSize: '11px', color: '#556', fontFamily: 'monospace', marginBottom: '10px',
    });
    info.textContent = `${world.playerCount} Spieler · Seed ${world.seed}`;
    card.appendChild(info);

    const btn = createButton(joined ? 'Betreten' : 'Beitreten');
    if (joined) {
      btn.style.background = '#1a3a2a';
      btn.style.borderColor = '#3a6a4a';
    }
    btn.addEventListener('click', () => {
      if (this._onSelect) this._onSelect(world.id);
    });
    card.appendChild(btn);
    this._buttons.push(btn);

    return card;
  }
}
